import _isEqual from "lodash/isEqual";
import AppPage, {
  ViewLocation,
  isViewLocation,
} from "../../../utils/models/AppPage";

// Helpers for the `general.appPages` device setting: plain JS in and out, never
// AppPage records.

const COLUMNS = [2, 3, 4];

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const byPositionThenName = (a, b) =>
  a.position - b.position ||
  String(a.name || "").localeCompare(String(b.name || ""));

// Tolerates a double-encoded JSON string, null or "".
export const normalizeAppPages = (value) => {
  const list = parseMaybeJson(parseMaybeJson(value));
  if (!Array.isArray(list)) return [];
  return list
    .filter((p) => p && typeof p === "object" && !Array.isArray(p))
    .map((p, index) => ({
      ...p,
      enabled: p.enabled !== false,
      position: Number.isFinite(p.position) ? p.position : index,
      location: isViewLocation(p.location) ? p.location : ViewLocation.menu,
    }))
    .sort(byPositionThenName);
};

// One location per device, stored on every view: the bottom bar wins.
export const readViewLocation = (pages) => {
  const list = pages || [];
  if (list.some((p) => p?.location === ViewLocation.bottomBar)) {
    return ViewLocation.bottomBar;
  }
  const first = list[0]?.location;
  return isViewLocation(first) ? first : ViewLocation.menu;
};

const isId = (id) => typeof id === "string" && id.length > 0;

// What the composer edits; `enabled`, `position` and `location` are set apart.
const viewContent = (p) => {
  const columns = Number(p.columns);
  return {
    name: String(p.name ?? ""),
    columns: COLUMNS.includes(columns) ? columns : 2,
    tiles: Array.isArray(p.tiles) ? p.tiles : [],
    data:
      p.data && typeof p.data === "object" && !Array.isArray(p.data)
        ? p.data
        : {},
  };
};

// Created once, never changed: the App follows the viewed page by id.
export const newAppPageId = (taken) => {
  let id;
  do {
    id = `view_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  } while (taken.has(id));
  return id;
};

// The only way a list is written. Missing or duplicate ids get fresh ones, since
// the App drops duplicates.
export const toAppPagesValue = (pages) => {
  const list = pages || [];
  const location = readViewLocation(list);
  const taken = new Set(list.map((p) => p.id).filter(isId));
  const seen = new Set();
  return list.map((p, index) => {
    let id = p.id;
    if (!isId(id) || seen.has(id)) {
      id = newAppPageId(taken);
      taken.add(id);
    }
    seen.add(id);
    return new AppPage({
      ...p,
      id,
      ...viewContent(p),
      enabled: p.enabled !== false,
      position: index,
      location,
    }).toJS();
  });
};

export const createAppPage = (pages, name) => [
  ...pages,
  {
    id: newAppPageId(new Set(pages.map((p) => p.id))),
    name,
    columns: 2,
    tiles: [],
    data: {},
    enabled: true,
  },
];

// Copies only composer fields; a view gone meanwhile is re-added, not lost.
export const replaceAppPage = (pages, record) => {
  const { id, name, columns, tiles, data } = record.toJS();
  const edited = { name, columns, tiles, data };
  const index = pages.findIndex((p) => p.id === id);
  return index < 0
    ? [...pages, { id, enabled: true, ...edited }]
    : pages.map((p, i) => (i === index ? { ...p, ...edited } : p));
};

export const removeAppPageAt = (pages, index) =>
  pages.filter((_, i) => i !== index);

// `offset` is -1 (up) or 1 (down).
export const moveAppPageAt = (pages, index, offset) => {
  const to = index + offset;
  if (index < 0 || to < 0 || to >= pages.length) return pages;
  const next = [...pages];
  const [page] = next.splice(index, 1);
  next.splice(to, 0, page);
  return next;
};

export const setAppPageEnabledAt = (pages, index, enabled) =>
  pages.map((p, i) => (i === index ? { ...p, enabled: !!enabled } : p));

export const setAppPagesLocation = (pages, location) =>
  pages.map((p) => ({ ...p, location }));

// Ids stay the storage keys, which the App already knows the views by.
export const importLegacyAppPages = (legacy) =>
  [...(legacy || [])]
    .filter((p) => isId(p?.id))
    .sort(byPositionThenName)
    .map(({ id, name, columns, tiles, data, enabled, location }) => ({
      id,
      name,
      columns,
      tiles,
      data,
      enabled,
      location,
    }));

// Each change between what the App has (`reported`) and the pending list
// (`desired`): the location, the order, and every added, edited, shown/hidden or
// deleted view. Drives the change count, the save dialog and the row markers.
export const describeAppPagesChanges = (reported, desired) => {
  const keyed = (value) =>
    normalizeAppPages(value).map((page, index) => ({
      key: isId(page.id) ? page.id : `#${index}`,
      page,
    }));
  const from = keyed(reported);
  const to = keyed(desired);
  const fromByKey = new Map(from.map((v) => [v.key, v.page]));
  const toKeys = new Set(to.map((v) => v.key));
  const changes = [];

  // Where views appear only matters while there are views.
  if (to.length) {
    const was = readViewLocation(from.map((v) => v.page));
    const now = readViewLocation(to.map((v) => v.page));
    if (was !== now) changes.push({ type: "location", from: was, to: now });
  }

  // Views on both sides only: an add or a delete alone is not a reorder.
  const keptFrom = from.filter((v) => toKeys.has(v.key));
  const keptTo = to.filter((v) => fromByKey.has(v.key));
  if (
    keptFrom.length !== keptTo.length ||
    keptFrom.some((v, i) => v.key !== keptTo[i].key)
  ) {
    changes.push({
      type: "order",
      from: keptFrom.map((v) => v.page.name),
      to: keptTo.map((v) => v.page.name),
    });
  }

  to.forEach(({ key, page }) => {
    const before = fromByKey.get(key);
    if (!before) {
      changes.push({ type: "added", id: key, name: page.name });
      return;
    }
    if (!_isEqual(viewContent(before), viewContent(page))) {
      changes.push({
        type: "edited",
        id: key,
        name: page.name,
        from: before.name,
      });
    }
    if (before.enabled !== page.enabled) {
      changes.push({
        type: "enabled",
        id: key,
        name: page.name,
        from: before.enabled,
        to: page.enabled,
      });
    }
  });

  from.forEach(({ key, page }) => {
    if (!toKeys.has(key))
      changes.push({ type: "deleted", id: key, name: page.name });
  });

  return changes;
};
